import { request } from "./http";
import { normalizeMalformedAegisUrl } from "../utils/aegisUrl";

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function unwrapItem(raw) {
  const source = asObject(raw);
  if (source.item && typeof source.item === "object") return source.item;
  return source;
}

function unwrapPage(raw) {
  const source = asObject(raw);
  const items = Array.isArray(source.items)
    ? source.items
    : Array.isArray(source.content)
      ? source.content
      : [];
  return {
    items,
    page: Number(source.page) || 0,
    size: Number(source.size) || items.length || 0,
    totalElements: Number(source.totalElements) || items.length,
    totalPages: Number(source.totalPages) || (items.length ? 1 : 0),
  };
}

function safeToken(value) {
  return encodeURIComponent(String(value || "").trim());
}

function pickText(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

function pickBool(...values) {
  for (const value of values) {
    if (typeof value === "boolean") return value;
    if (value === 1 || value === 0) return Boolean(value);
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true" || normalized === "1") return true;
      if (normalized === "false" || normalized === "0") return false;
    }
  }
  return null;
}

const CLOSED_ROOM_STATUS_SET = new Set(["CLOSED", "ENDED", "COMPLETED", "COMPLETE", "DONE", "FINISHED", "CANCELLED", "CANCELED"]);

function canFallbackEndpoint(error) {
  const status = Number(error?.status ?? error?.response?.status ?? 0);
  return status === 404 || status === 405 || status === 501;
}

function canFallbackToPublicEndpoint(error) {
  const status = Number(error?.status ?? error?.response?.status ?? 0);
  return status === 401 || status === 403 || canFallbackEndpoint(error);
}

function normalizeAegisPayload(raw) {
  const source = asObject(raw);
  const aegis = asObject(source.aegis || source.aegisData || source.realtime || source.conference || source.meta);

  const joinUrl = pickText(
    source.joinUrl,
    source.join_url,
    source.aegisJoinUrl,
    source.aegis_join_url,
    source.embedUrl,
    source.embed_url,
    source.url,
    aegis.joinUrl,
    aegis.join_url,
    aegis.aegisJoinUrl,
    aegis.aegis_join_url,
    aegis.embedUrl,
    aegis.embed_url,
    aegis.url
  );

  const playerUrl = pickText(
    source.playerUrl,
    source.player_url,
    source.aegisPlayerUrl,
    source.aegis_player_url,
    source.embedUrl,
    source.embed_url,
    source.url,
    aegis.playerUrl,
    aegis.player_url,
    aegis.aegisPlayerUrl,
    aegis.aegis_player_url,
    aegis.embedUrl,
    aegis.embed_url,
    aegis.url
  );

  const transcriptUrl = pickText(
    source.transcriptUrl,
    source.transcript_url,
    source.aegisTranscriptUrl,
    source.aegis_transcript_url,
    aegis.transcriptUrl,
    aegis.transcript_url,
    aegis.aegisTranscriptUrl,
    aegis.aegis_transcript_url
  );

  const transcriptStatus = pickText(
    source.transcriptStatus,
    source.transcript_status,
    source.aegisTranscriptStatus,
    source.aegis_transcript_status,
    aegis.transcriptStatus,
    aegis.transcript_status,
    aegis.aegisTranscriptStatus,
    aegis.aegis_transcript_status
  );

  const joinAvailableNow = pickBool(
    source.joinAvailableNow,
    source.join_available_now,
    source.aegisJoinAvailableNow,
    source.aegis_join_available_now,
    source.availableNow,
    source.available_now,
    aegis.joinAvailableNow,
    aegis.join_available_now,
    aegis.aegisJoinAvailableNow,
    aegis.aegis_join_available_now,
    aegis.availableNow,
    aegis.available_now
  );

  const joinBlockedReason = pickText(
    source.joinBlockedReason,
    source.join_blocked_reason,
    source.aegisJoinBlockedReason,
    source.aegis_join_blocked_reason,
    source.blockedReason,
    source.blocked_reason,
    aegis.joinBlockedReason,
    aegis.join_blocked_reason,
    aegis.aegisJoinBlockedReason,
    aegis.aegis_join_blocked_reason,
    aegis.blockedReason,
    aegis.blocked_reason
  );

  const joinBlockedMessage = pickText(
    source.joinBlockedMessage,
    source.join_blocked_message,
    source.aegisJoinBlockedMessage,
    source.aegis_join_blocked_message,
    source.blockedMessage,
    source.blocked_message,
    aegis.joinBlockedMessage,
    aegis.join_blocked_message,
    aegis.aegisJoinBlockedMessage,
    aegis.aegis_join_blocked_message,
    aegis.blockedMessage,
    aegis.blocked_message
  );

  const joinAvailableFromAt = pickText(
    source.joinAvailableFromAt,
    source.join_available_from_at,
    source.aegisJoinAvailableFromAt,
    source.aegis_join_available_from_at,
    source.opensAt,
    source.opens_at,
    aegis.joinAvailableFromAt,
    aegis.join_available_from_at,
    aegis.aegisJoinAvailableFromAt,
    aegis.aegis_join_available_from_at,
    aegis.opensAt,
    aegis.opens_at
  );

  return {
    joinUrl: normalizeMalformedAegisUrl(joinUrl),
    playerUrl: normalizeMalformedAegisUrl(playerUrl),
    transcriptUrl: normalizeMalformedAegisUrl(transcriptUrl),
    transcriptStatus,
    joinAvailableNow,
    joinBlockedReason,
    joinBlockedMessage,
    joinAvailableFromAt,
  };
}

function normalizeRoom(raw) {
  const source = unwrapItem(raw);
  const room = asObject(source.sessionRoom || source.session_room || source.room || source);
  const normalized = { ...room };
  const booking = asObject(room.booking || room.appointment || room.session || source.booking || source.appointment || source.session || source.conference);
  const nestedRoom = asObject(source.sessionRoom || source.session_room || source.room || room.sessionRoom || room.session_room || room.room);
  const aegis = normalizeAegisPayload({ ...source, ...booking, ...nestedRoom, ...room });

  normalized.roomToken =
    pickText(
      room.roomToken,
      room.room_token,
      room.token,
      room.sessionRoomToken,
      room.session_room_token,
      source.roomToken,
      source.room_token
    ) || normalized.roomToken;

  normalized.chatDialogId =
    pickText(
      room.chatDialogId,
      room.chat_dialog_id,
      room.dialogId,
      room.dialog_id,
      room.chatId,
      room.chat_id
    ) || normalized.chatDialogId;

  normalized.sessionStartAt =
    pickText(
      room.sessionStartAt,
      room.session_start_at,
      room.startAt,
      room.start_at,
      room.startTime,
      room.start_time,
      room.scheduledAt,
      room.scheduled_at,
      room.scheduledStartAt,
      room.scheduled_start_at,
      booking.sessionStartAt,
      booking.session_start_at,
      booking.startAt,
      booking.start_at,
      booking.startTime,
      booking.start_time,
      source.sessionStartAt,
      source.session_start_at,
      source.startAt,
      source.start_at,
      source.startedAt,
      source.started_at,
      source.scheduledAt,
      source.scheduled_at,
      nestedRoom.sessionStartAt,
      nestedRoom.session_start_at,
      nestedRoom.startAt,
      nestedRoom.start_at,
      nestedRoom.scheduledAt,
      nestedRoom.scheduled_at
    ) || normalized.sessionStartAt;

  normalized.sessionEndAt =
    pickText(
      room.sessionEndAt,
      room.session_end_at,
      room.endAt,
      room.end_at,
      room.endTime,
      room.end_time,
      room.scheduledEndAt,
      room.scheduled_end_at,
      booking.sessionEndAt,
      booking.session_end_at,
      booking.endAt,
      booking.end_at,
      booking.endTime,
      booking.end_time,
      source.sessionEndAt,
      source.session_end_at,
      source.endAt,
      source.end_at,
      nestedRoom.sessionEndAt,
      nestedRoom.session_end_at,
      nestedRoom.endAt,
      nestedRoom.end_at,
      nestedRoom.scheduledEndAt,
      nestedRoom.scheduled_end_at
    ) || normalized.sessionEndAt;

  const statusCandidates = [
    room.status,
    room.state,
    room.roomStatus,
    room.room_status,
    room.sessionStatus,
    room.session_status,
    booking.status,
    booking.state,
    booking.bookingStatus,
    booking.booking_status,
    booking.sessionStatus,
    booking.session_status,
    source.status,
    source.state,
    source.roomStatus,
    source.room_status,
    source.sessionStatus,
    source.session_status,
    nestedRoom.status,
    nestedRoom.state,
    nestedRoom.roomStatus,
    nestedRoom.room_status,
    nestedRoom.sessionStatus,
    nestedRoom.session_status,
  ].map((value) => String(value || "").trim()).filter(Boolean);
  normalized.status =
    statusCandidates.find((status) => CLOSED_ROOM_STATUS_SET.has(String(status).toUpperCase())) ||
    statusCandidates[0] ||
    normalized.status;

  if (aegis.joinUrl) normalized.aegisJoinUrl = aegis.joinUrl;
  if (aegis.playerUrl) normalized.aegisPlayerUrl = aegis.playerUrl;
  if (aegis.transcriptUrl) normalized.aegisTranscriptUrl = aegis.transcriptUrl;
  if (aegis.transcriptStatus) normalized.aegisTranscriptStatus = aegis.transcriptStatus;
  if (aegis.joinAvailableNow !== null) normalized.aegisJoinAvailableNow = aegis.joinAvailableNow;
  if (aegis.joinBlockedReason) normalized.aegisJoinBlockedReason = aegis.joinBlockedReason;
  if (aegis.joinBlockedMessage) normalized.aegisJoinBlockedMessage = aegis.joinBlockedMessage;
  if (aegis.joinAvailableFromAt) normalized.aegisJoinAvailableFromAt = aegis.joinAvailableFromAt;

  return normalized;
}

function normalizeCompleteResponse(raw) {
  const source = unwrapItem(raw);
  const conference = source?.conference && typeof source.conference === "object"
    ? source.conference
    : source?.room && typeof source.room === "object"
      ? source.room
      : source;

  const finalTranscript =
    source?.final_transcript ??
    source?.finalTranscript ??
    source?.transcript ??
    null;

  const exportStatus =
    source?.export_status ??
    source?.exportStatus ??
    source?.export ??
    null;

  return {
    room: normalizeRoom(conference),
    finalTranscript,
    exportStatus,
    raw: source,
  };
}

function normalizeAegisStatus(raw) {
  const source = unwrapItem(raw);
  const normalized = { ...source };
  const aegis = normalizeAegisPayload(source);

  normalized.joinUrl = aegis.joinUrl || normalized.joinUrl || "";
  normalized.playerUrl = aegis.playerUrl || normalized.playerUrl || "";
  normalized.transcriptUrl = aegis.transcriptUrl || normalized.transcriptUrl || "";
  normalized.transcriptStatus = aegis.transcriptStatus || normalized.transcriptStatus || "";
  if (aegis.joinAvailableNow !== null) normalized.joinAvailableNow = aegis.joinAvailableNow;
  if (aegis.joinBlockedReason) normalized.joinBlockedReason = aegis.joinBlockedReason;
  if (aegis.joinBlockedMessage) normalized.joinBlockedMessage = aegis.joinBlockedMessage;
  if (aegis.joinAvailableFromAt) normalized.joinAvailableFromAt = aegis.joinAvailableFromAt;

  return normalized;
}

export const sessionRoomApi = {
  listMyRooms: async ({ page = 0, size = 200 } = {}) => {
    const raw = await request(
      `/api/session/rooms/mine?page=${Math.max(0, Number(page) || 0)}&size=${Math.min(200, Math.max(1, Number(size) || 200))}`
    );
    const pageData = unwrapPage(raw);
    return {
      ...pageData,
      items: (pageData.items || []).map((item) => normalizeRoom(item)),
    };
  },

  openByBooking: async (bookingId) => {
    const id = String(bookingId || "").trim();
    if (!id) throw new Error("bookingId is required");
    const raw = await request(`/api/session/rooms/${safeToken(id)}`, { method: "POST" });
    return normalizeRoom(raw);
  },

  openOrGetByBooking: async (bookingId) => {
    const id = String(bookingId || "").trim();
    if (!id) throw new Error("bookingId is required");
    const raw = await request(`/api/session/rooms/by-booking/${safeToken(id)}`);
    return normalizeRoom(raw);
  },

  getRoom: async (roomToken) => {
    const token = String(roomToken || "").trim();
    if (!token) throw new Error("roomToken is required");
    const raw = await request(`/api/session/room/${safeToken(token)}`);
    return normalizeRoom(raw);
  },

  getPublicRoom: async (roomToken) => {
    const token = String(roomToken || "").trim();
    if (!token) throw new Error("roomToken is required");
    const raw = await request(`/api/public/session/room/${safeToken(token)}`);
    return normalizeRoom(raw);
  },

  joinByInvite: async (roomToken, participantPayload = null) => {
    const token = String(roomToken || "").trim();
    if (!token) throw new Error("roomToken is required");
    const payload = participantPayload && typeof participantPayload === "object" ? participantPayload : null;
    const requestOptions = payload ? { method: "POST", json: payload } : { method: "POST" };
    const privatePath = `/api/session/room/${safeToken(token)}/join`;
    const publicPath = `/api/public/session/room/${safeToken(token)}/join`;

    if (payload) {
      try {
        const raw = await request(publicPath, requestOptions);
        return normalizeRoom(raw);
      } catch (error) {
        if (!canFallbackToPublicEndpoint(error)) throw error;
        const raw = await request(privatePath, requestOptions);
        return normalizeRoom(raw);
      }
    }

    try {
      const raw = await request(privatePath, requestOptions);
      return normalizeRoom(raw);
    } catch (error) {
      if (!canFallbackToPublicEndpoint(error)) throw error;
      const raw = await request(publicPath, requestOptions);
      return normalizeRoom(raw);
    }
  },

  getInviteLink: async (roomToken) => {
    const token = String(roomToken || "").trim();
    if (!token) throw new Error("roomToken is required");
    const raw = await request(`/api/session/room/${safeToken(token)}/invite-link`);
    return unwrapItem(raw);
  },

  closeRoom: async (roomToken) => {
    const token = String(roomToken || "").trim();
    if (!token) throw new Error("roomToken is required");
    const raw = await request(`/api/session/room/${safeToken(token)}/close`, { method: "POST" });
    return normalizeRoom(raw);
  },

  completeRoom: async (roomToken, options = {}) => {
    const token = String(roomToken || "").trim();
    if (!token) throw new Error("roomToken is required");

    const {
      includeInterim = false,
      waitExport = true,
      forceExport = true,
      exportCallbackUrl = "",
      exportCallbackSecret = "",
      timeoutMs = 90000,
    } = options || {};

    const qs = new URLSearchParams();
    qs.set("include_interim", includeInterim ? "true" : "false");
    qs.set("wait_export", waitExport ? "true" : "false");
    qs.set("force_export", forceExport ? "true" : "false");
    if (String(exportCallbackUrl || "").trim()) qs.set("export_callback_url", String(exportCallbackUrl).trim());
    if (String(exportCallbackSecret || "").trim()) qs.set("export_callback_secret", String(exportCallbackSecret).trim());
    const path = `/api/session/room/${safeToken(token)}/complete?${qs.toString()}`;

    try {
      const raw = await request(path, { method: "POST", timeoutMs: Math.max(30000, Number(timeoutMs) || 90000) });
      return normalizeCompleteResponse(raw);
    } catch (error) {
      if (!canFallbackEndpoint(error)) throw error;
      const room = await sessionRoomApi.closeRoom(token);
      return { room, finalTranscript: null, exportStatus: null, raw: null };
    }
  },

  leaveReview: async (roomToken, { rating, comment = "", isPublic = true } = {}) => {
    const token = String(roomToken || "").trim();
    const normalizedRating = Number(rating);
    if (!token) throw new Error("roomToken is required");
    if (!Number.isFinite(normalizedRating) || normalizedRating < 1 || normalizedRating > 5) {
      throw new Error("rating must be in range 1..5");
    }
    const raw = await request(`/api/session/room/${safeToken(token)}/review`, {
      method: "POST",
      json: {
        rating: Math.round(normalizedRating),
        comment: String(comment || "").trim(),
        isPublic: Boolean(isPublic),
      },
    });
    return unwrapItem(raw);
  },

  getAegisStatus: async (roomToken) => {
    const token = String(roomToken || "").trim();
    if (!token) throw new Error("roomToken is required");
    const raw = await request(`/api/session/room/${safeToken(token)}/aegis/status`);
    return normalizeAegisStatus(raw);
  },

  syncAegisStatus: async (roomToken) => {
    const token = String(roomToken || "").trim();
    if (!token) throw new Error("roomToken is required");
    const raw = await request(`/api/session/room/${safeToken(token)}/aegis/sync`, { method: "POST" });
    return normalizeAegisStatus(raw);
  },

  listTimeline: async (roomToken, { page = 0, size = 50 } = {}) => {
    const token = String(roomToken || "").trim();
    if (!token) throw new Error("roomToken is required");
    const raw = await request(
      `/api/session/room/${safeToken(token)}/timeline?page=${Math.max(0, Number(page) || 0)}&size=${Math.max(1, Number(size) || 50)}`
    );
    return unwrapPage(raw);
  },

  listMyNotes: async (roomToken, { page = 0, size = 100 } = {}) => {
    const token = String(roomToken || "").trim();
    if (!token) throw new Error("roomToken is required");
    const raw = await request(
      `/api/session/room/${safeToken(token)}/notes/me?page=${Math.max(0, Number(page) || 0)}&size=${Math.max(1, Number(size) || 100)}`
    );
    return unwrapPage(raw);
  },

  listResources: async (roomToken, { page = 0, size = 100 } = {}) => {
    const token = String(roomToken || "").trim();
    if (!token) throw new Error("roomToken is required");
    const raw = await request(
      `/api/session/room/${safeToken(token)}/resources?page=${Math.max(0, Number(page) || 0)}&size=${Math.max(1, Number(size) || 100)}`
    );
    return unwrapPage(raw);
  },

  createMyNote: async (roomToken, content) => {
    const token = String(roomToken || "").trim();
    if (!token) throw new Error("roomToken is required");
    const raw = await request(`/api/session/room/${safeToken(token)}/notes/me`, {
      method: "POST",
      json: { content: String(content || "") },
    });
    return unwrapItem(raw);
  },

  updateMyNote: async (roomToken, noteId, content) => {
    const token = String(roomToken || "").trim();
    const id = String(noteId || "").trim();
    if (!token || !id) throw new Error("roomToken and noteId are required");
    const raw = await request(`/api/session/room/${safeToken(token)}/notes/me/${safeToken(id)}`, {
      method: "PUT",
      json: { content: String(content || "") },
    });
    return unwrapItem(raw);
  },

  deleteMyNote: async (roomToken, noteId) => {
    const token = String(roomToken || "").trim();
    const id = String(noteId || "").trim();
    if (!token || !id) throw new Error("roomToken and noteId are required");
    return request(`/api/session/room/${safeToken(token)}/notes/me/${safeToken(id)}`, {
      method: "DELETE",
    });
  },
};
